import { HCP_CUBE_NAME } from '@/api/hcp/hcp/constants';
import { isNonEmptyString, isStringArray } from '@/api/queries/utils';
import { SESSION_STORAGE_SYNC_EVENT } from '@/hooks/useSessionStorage';
import { GetOptionsQueryFunction } from '@/types/cube';
import { FilterConfigDynamic } from '@/types/filter';
import { SelectItem } from '@/types/ui';
import { Logger } from '@/utils/Logger';
import { sortBy } from 'lodash-es';
import { extractCountryName, getSingleUserCountryLabel, resolveCountryCodes } from '../../utils/countryFilterHelpers';
import expandHierarchicalPaths from '../../utils/expandHierarchicalPaths';

export const RESOLVED_COUNTRY_CODES_KEY = 'RESOLVED_COUNTRY_CODES';
const LOGGER_MODULE = 'countryFilter';

const PRIMITIVE_NODE_VALUE_TYPES = ['string', 'number', 'boolean'];

// Cube returns untyped rows at runtime, so non-primitive segments are still guarded against here.
export function mapCountryNodeValue(value: unknown): string | null {
  if (!PRIMITIVE_NODE_VALUE_TYPES.includes(typeof value)) {
    return null;
  }

  const stringValue = String(value).trim();

  return stringValue || null;
}

function parseStoredCountryCodes(rawStoredCodes: string | null): string[] {
  if (!rawStoredCodes) {
    return [];
  }

  try {
    const parsedValue: unknown = JSON.parse(rawStoredCodes);

    if (!Array.isArray(parsedValue)) {
      return [];
    }

    return parsedValue.every(parsedCode => typeof parsedCode === 'string') ? parsedValue : [];
  } catch {
    return [];
  }
}

export const readCachedCountryCodes = (): string[] => {
  try {
    return parseStoredCountryCodes(sessionStorage.getItem(RESOLVED_COUNTRY_CODES_KEY));
  } catch (error) {
    Logger.error(LOGGER_MODULE, 'Failed to read resolved country codes from sessionStorage', error);
    return [];
  }
};

function emitCountryCodesSyncEvent(): void {
  window.dispatchEvent(new CustomEvent(SESSION_STORAGE_SYNC_EVENT, { detail: { key: RESOLVED_COUNTRY_CODES_KEY } }));
}

export const writeCachedCountryCodes = (codes: string[]): void => {
  try {
    const currentRaw = sessionStorage.getItem(RESOLVED_COUNTRY_CODES_KEY);

    if (codes.length === 0) {
      // Nothing stored already: no change, so do not emit (avoids re-render loops
      // when this runs during render, e.g. from useGetFilterOptions priming).
      if (currentRaw === null) return;
      sessionStorage.removeItem(RESOLVED_COUNTRY_CODES_KEY);
      emitCountryCodesSyncEvent();
      return;
    }

    const nextRaw = JSON.stringify(codes);
    // Value unchanged: skip the write and the event so listeners are not woken up
    // on every render, which would otherwise trigger an update loop.
    if (currentRaw === nextRaw) return;

    sessionStorage.setItem(RESOLVED_COUNTRY_CODES_KEY, nextRaw);
  } catch (error) {
    Logger.error(LOGGER_MODULE, 'Failed to persist resolved country codes to sessionStorage', error);
    return;
  }

  emitCountryCodesSyncEvent();
};

const dimensions = [
  `${HCP_CUBE_NAME}.roche_business_region`,
  `${HCP_CUBE_NAME}.roche_affiliate_name`,
  `${HCP_CUBE_NAME}.country_name`,
  `${HCP_CUBE_NAME}.country_code`,
];

const COUNTRY_TREE_OPTIONS: GetOptionsQueryFunction = ({}) => ({
  dimensions,
  limit: 1000,
});

// Stable empty reference so an absent `userCountries` always normalizes to the same value;
// the placeholder cache guard below relies on referential identity to avoid rebuilding.
const EMPTY_USER_COUNTRIES: SelectItem<string>[] = [];

let cachedCountryOptions: SelectItem<string>[] = [];
let cachedUserCountries: SelectItem<string>[] = [];
let cachedUserCountryMap: Map<string, string> = new Map();

function setCachedUserCountries(userCountries: SelectItem<string>[]): void {
  cachedUserCountries = userCountries;
  cachedUserCountryMap = new Map();

  userCountries.forEach(country => {
    if (typeof country.label === 'string') {
      cachedUserCountryMap.set(country.label, country.value);
    }
  });
}

/**
 * Primes the user-countries cache from outside the country dropdown component.
 *
 * Country-code resolution normally warms these module caches as a side effect of
 * the country dropdown rendering. On pages where that dropdown is not mounted
 * (e.g. Channel Focus channel-specific filters), the cache stays cold and
 * `applyFilter` cannot resolve `hcp.country_code`, so global-country cascading
 * silently stops narrowing those option queries. Priming centrally keeps
 * resolution working regardless of the dropdown's mount state.
 */
export const primeUserCountriesCache = (userCountries: SelectItem<string>[] | undefined): void => {
  if (!userCountries?.length) return;
  if (cachedUserCountries === userCountries) return;
  setCachedUserCountries(userCountries);
};

function resolveCachedCountryCodes(countryValues: string[]): string[] {
  if (countryValues.length === 0) {
    return [];
  }

  if (cachedCountryOptions.length > 0 && cachedUserCountries.length > 0) {
    return [
      ...new Set(
        countryValues.flatMap(countryValue =>
          resolveCountryCodes(countryValue, cachedCountryOptions, cachedUserCountries),
        ),
      ),
    ].sort();
  }

  if (cachedUserCountries.length > 0) {
    const resolvedCodes = new Set<string>();

    countryValues.forEach(countryValue => {
      const countryCode = cachedUserCountryMap.get(extractCountryName(countryValue));

      if (isNonEmptyString(countryCode)) {
        resolvedCodes.add(countryCode);
      }
    });

    return [...resolvedCodes].sort();
  }

  return [];
}

export const syncCachedCountryCodes = (countryValues: string[]): string[] => {
  // Empty selection means the country filter was cleared — clear the cache too.
  if (countryValues.length === 0) {
    writeCachedCountryCodes([]);
    return [];
  }

  const resolvedCountryCodes = resolveCachedCountryCodes(countryValues);

  // A non-empty selection that resolves to no codes means the module caches are
  // cold (the country dropdown never mounted). Do not overwrite previously
  // persisted codes with an empty list — the earlier value is still correct and
  // is what `applyFilter` falls back to on pages without the dropdown.
  if (resolvedCountryCodes.length === 0) {
    return readCachedCountryCodes();
  }

  writeCachedCountryCodes(resolvedCountryCodes);

  return resolvedCountryCodes;
};

const countryFilter: FilterConfigDynamic = {
  label: 'global.filters.formControls.countryLabel',
  sourceCube: HCP_CUBE_NAME,
  defaultValue: [],
  getOptionsQuery: COUNTRY_TREE_OPTIONS,
  placeholder: ({ userInfo, userCountries }) => {
    const normalizedUserCountries = userCountries ?? EMPTY_USER_COUNTRIES;
    if (cachedUserCountries !== normalizedUserCountries) {
      setCachedUserCountries(normalizedUserCountries);
    }

    const label = getSingleUserCountryLabel(userCountries, userInfo?.countries ?? []);
    if (label) return String(label);
    return 'global.filters.formControls.countryPlaceholder';
  },
  mapResult: countryNode => {
    const region = mapCountryNodeValue(countryNode[dimensions[0]]);
    const affiliate = mapCountryNodeValue(countryNode[dimensions[1]]);
    const country = mapCountryNodeValue(countryNode[dimensions[2]]);

    const path = [region, affiliate, country];

    const result = path.filter(Boolean).join('__');

    return {
      label: result,
      value: result,
    };
  },
  processOptions: options => {
    const processed = expandHierarchicalPaths(options);
    cachedCountryOptions = processed;
    return processed;
  },
  type: 'oldtreeselect',
  clearable: true,
  searchable: true,
  collapseSingleChild: true,
  applyDynamicFilters: true,
  sort: unsortedData => {
    return sortBy(unsortedData, 'label');
  },
  disabled: ({ contexts: { userInfo } }) => {
    return userInfo?.countries?.length === 1;
  },
  format: ({ value }) => {
    if (!isNonEmptyString(value)) return [];
    return extractCountryName(value);
  },
  applyFilter: countryValues => {
    if (!isStringArray(countryValues) || countryValues.length === 0) {
      writeCachedCountryCodes([]);
      return;
    }

    const hasLiveCache = cachedCountryOptions.length > 0 && cachedUserCountries.length > 0;

    const resolvedCountryCodes = hasLiveCache ? resolveCachedCountryCodes(countryValues) : [];
    const hasResolvedCountryCodes = resolvedCountryCodes.length > 0;

    // Resolving to nothing means other active filters narrowed the live options — never overwrite.
    if (hasResolvedCountryCodes) writeCachedCountryCodes(resolvedCountryCodes);

    const countryCodes = hasResolvedCountryCodes ? resolvedCountryCodes : readCachedCountryCodes();

    if (!countryCodes.length) return;

    return {
      member: `${HCP_CUBE_NAME}.country_code`,
      operator: 'equals',
      values: countryCodes,
    };
  },
};

export default countryFilter;
